import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import ConfirmDelete from "@/components/ConfirmDelete";
import { Undo2, Trash2, Archive } from "lucide-react";
import { toast } from "sonner";
import SkeletonPage, { SkeletonCard, SkeletonTable } from "@/components/KovaSkeleton";

const ENTITY_LABELS = {
  customers: "Customer",
  products: "Product",
  sales: "Sale",
  services: "Service",
  followups: "Follow-up",
  customer_fields: "Customer field",
  product_fields: "Product field",
};

export default function Trash() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/trash");
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load trash");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const restore = async (item) => {
    try {
      await api.post(`/trash/${item.entity}/${item.id}/restore`);
      toast.success(`Restored: ${item.label}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to restore");
    }
  };

  const purge = async (item) => {
    try {
      await api.delete(`/trash/${item.entity}/${item.id}`);
      toast.success("Permanently deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to purge");
    }
  };

  const groups = Array.from(new Set(rows.map((r) => r.entity)));
  const visibleRows = filter === "all" ? rows : rows.filter((r) => r.entity === filter);

  return (
    <div className="p-8 lg:p-10 max-w-[1400px]">
      <PageHeader
        title="Recently Deleted"
        subtitle="Records you deleted can be restored here. Purge to remove permanently."
        testid="page-trash-header"
      />

      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => setFilter("all")}
          data-testid="trash-filter-all"
          className={`px-3 py-1.5 rounded-md text-xs font-heading font-medium border ${
            filter === "all"
              ? "bg-[#39FF14] text-[#0B0F0C] border-[#39FF14]/40"
              : "bg-[#121815] border-[#3A4640] text-[#F8F9FA] hover:bg-[#0B0F0C]"
          }`}
        >
          All ({rows.length})
        </button>
        {groups.map((g) => (
          <button
            key={g}
            onClick={() => setFilter(g)}
            data-testid={`trash-filter-${g}`}
            className={`px-3 py-1.5 rounded-md text-xs font-heading font-medium border ${
              filter === g
                ? "bg-[#39FF14] text-[#0B0F0C] border-[#39FF14]/40"
                : "bg-[#121815] border-[#3A4640] text-[#F8F9FA] hover:bg-[#0B0F0C]"
            }`}
          >
            {ENTITY_LABELS[g] || g} ({rows.filter((r) => r.entity === g).length})
          </button>
        ))}
      </div>

      <div className="bg-[#121815] border border-[#3A4640] rounded-md shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0B0F0C] border-b border-[#3A4640] text-xs uppercase tracking-wider text-[#8A8F98] font-heading">
            <tr>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Record</th>
              <th className="text-left px-4 py-3">Detail</th>
              <th className="text-left px-4 py-3">Deleted</th>
              <th className="text-left px-4 py-3">By</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="trash-table-body">
            {loading && (
              <tr>
                <td colSpan={6}><div className="p-4"><SkeletonTable rows={6} cols={5} /></div></td>
              </tr>
            )}
            {!loading && visibleRows.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-12 text-[#8A8F98]">
                  <Archive className="mx-auto mb-2" />
                  Nothing here. Deleted records will appear in this list.
                </td>
              </tr>
            )}
            {visibleRows.map((r) => (
              <tr
                key={`${r.entity}-${r.id}`}
                className="border-b border-[#3A4640] last:border-0 hover:bg-[#0B0F0C]"
                data-testid={`trash-row-${r.entity}-${r.id}`}
              >
                <td className="px-4 py-3">
                  <span className="text-xs bg-[#121815] px-2 py-1 rounded font-heading">
                    {ENTITY_LABELS[r.entity] || r.entity}
                  </span>
                </td>
                <td className="px-4 py-3 font-medium text-[#F8F9FA]">{r.label || "-"}</td>
                <td className="px-4 py-3 text-[#8A8F98]">{r.sublabel || "-"}</td>
                <td className="px-4 py-3 text-[#8A8F98] text-xs">{formatDate(r.deleted_at)}</td>
                <td className="px-4 py-3 text-[#8A8F98] text-xs">{r.deleted_by || "-"}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => restore(r)}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-[#3A4640] hover:bg-green-50 text-[#39FF14]"
                    data-testid={`trash-restore-${r.entity}-${r.id}`}
                  >
                    <Undo2 size={12} /> Restore
                  </button>
                  <ConfirmDelete
                    itemLabel={r.label}
                    entityLabel={`${(ENTITY_LABELS[r.entity] || r.entity).toLowerCase()} permanently`}
                    onConfirm={() => purge(r)}
                    testid={`purge-${r.entity}-${r.id}`}
                  >
                    <button
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-[#3A4640] hover:bg-red-500/10 text-red-600 ml-2"
                      data-testid={`trash-purge-${r.entity}-${r.id}`}
                    >
                      <Trash2 size={12} /> Purge
                    </button>
                  </ConfirmDelete>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
